import { action, observable, makeObservable, reaction } from 'mobx';

import { v4 as uuidv4 } from 'uuid';

import { set } from 'lodash';

import { AxiosError } from 'axios';

import RootStore from 'stores/Root';
import API from 'utils/api';

import {
  EntityFieldType,
  AssignmentRulePipelineResponseType,
  AssignmentRulePipelineRequestType,
  AssignmentRuleResponseType,
  OPERATORS,
} from 'utils/api/modules/entities/entities.types';

import { EntityModules, ProtectorType } from 'core/constants';

import { apiErrors } from 'utils/api/errors';

import {
  areRuleArraysEqual,
  areRulePipelineArraysEqual,
  areRulesEqual,
  getNextRuleName,
  transformRuleForRequest,
  transformValues,
} from './helpers';

export enum AssignmentRuleTypeEnum {
  Collaborators = 'collaborators',
  Pipeline = 'pipeline',
}

export type AssignmentRuleType = AssignmentRuleResponseType | AssignmentRulePipelineResponseType;

// Mirrors the backend's GROUP_TARGETS_REMOVED_DETAIL so both surfaces reject with the same wording.
export const GROUP_TARGETS_REMOVED_ERROR =
  'Groups are no longer supported as assignment rule targets. Remove the group to save.';

// Type guards
export const isAssignmentRuleResponseType = (rule: AssignmentRuleType): rule is AssignmentRuleResponseType =>
  'targetUsers' in rule;

export const isAssignmentRulePipelineResponseType = (
  rule: AssignmentRuleType
): rule is AssignmentRulePipelineResponseType => 'pipeline' in rule;

class AssignmentRules {
  rootStore: RootStore;

  api: typeof API;

  @observable assignmentRules: AssignmentRuleResponseType[] = [];

  @observable initialAssignmentRules: AssignmentRuleResponseType[] = [];

  @observable assignmentRulesPipelines: AssignmentRulePipelineResponseType[] = [];

  @observable initialAssignmentRulesPipelines: AssignmentRulePipelineResponseType[] = [];

  @observable edited: boolean = false;

  @observable isLoading: boolean = false;

  @observable error: string | null = null;

  @observable fields: EntityFieldType[] = [];

  @observable lastPublished: string | null = null;

  @observable isSaving: boolean = false;

  @observable assignmentRulesPipelinesErrors: string[] = [];

  @observable assignmentRulesErrors: string[] = [];

  @observable ruleErrors: { [ruleUuid: string]: { fieldKey: string; conditionIndex: number; error: string } } = {};

  constructor(rootStore: RootStore, api: typeof API) {
    makeObservable(this);
    this.rootStore = rootStore;
    this.api = api;

    reaction(
      () => [JSON.stringify(this.assignmentRules), JSON.stringify(this.assignmentRulesPipelines)],
      () => {
        const regularRulesEqual = areRuleArraysEqual(this.assignmentRules, this.initialAssignmentRules);
        const pipelineRulesEqual = areRulePipelineArraysEqual(
          this.assignmentRulesPipelines,
          this.initialAssignmentRulesPipelines
        );

        this.edited = !(regularRulesEqual && pipelineRulesEqual);
      }
    );
  }

  @action.bound
  async getAssignmentRules(): Promise<void> {
    this.isLoading = true;
    try {
      const { data } = await this.api.entities.getAssignmentRules()();
      this.assignmentRules = transformValues<AssignmentRuleResponseType[]>(data);
      this.initialAssignmentRules = data;

      // load assignment rules pipelines
      const { data: assignmentRulesPipelines } = await this.api.entities.getAssignmentRulesPipelines()();
      this.assignmentRulesPipelines = transformValues<AssignmentRulePipelineResponseType[]>(assignmentRulesPipelines);
      this.initialAssignmentRulesPipelines = assignmentRulesPipelines;

      // load fields
      const {
        data: { results },
      } = await this.api.entities.loadEntityFields(EntityModules.Events, {
        pagination: 'false',
        protector_type: `${ProtectorType.SingleChoice},${ProtectorType.MultiChoice},${ProtectorType.Lookup}`,
      })();

      const formName = {
        key: OPERATORS.submission_form,
        name: 'Form Name',
        protectorType: ProtectorType.SingleChoice,
      } as EntityFieldType;

      this.fields = [formName, ...results];

      this.lastPublished = data[0] ? data[0].updatedAt : null;
    } catch (error) {
      this.error = error as string;
    } finally {
      this.isLoading = false;
    }
  }

  isRuleEdited(rule: AssignmentRuleResponseType): boolean {
    const initial = this.initialAssignmentRules.find((r) => r.uuid === rule.uuid);
    return initial === undefined || !areRulesEqual(rule, initial);
  }

  validateRule(): boolean {
    let hasErrors = false;
    // validate regular assignment rules
    this.assignmentRules.forEach((rule) => {
      if (rule.conditions.length === 0) {
        this.rootStore.notification.enqueueErrorSnackbar('Rule must have at least one condition');
        hasErrors = true;
      }
      if (rule.targetUsers.length === 0 && (rule.targetGroups || []).length === 0) {
        this.rootStore.notification.enqueueErrorSnackbar('Rule must have at least one target user');
        hasErrors = true;
      }
      if ((rule.targetGroups || []).length > 0 && this.isRuleEdited(rule)) {
        this.rootStore.notification.enqueueErrorSnackbar(`"${rule.name}": ${GROUP_TARGETS_REMOVED_ERROR}`);
        hasErrors = true;
        this.ruleErrors[rule.uuid] = {
          fieldKey: 'targetGroups',
          conditionIndex: -1,
          error: GROUP_TARGETS_REMOVED_ERROR,
        };
      }
      if (rule.name.length === 0) {
        this.rootStore.notification.enqueueErrorSnackbar('Rule must have a name');
        hasErrors = true;
      }
      rule.conditions.forEach((condition, index) => {
        const operator = Object.keys(condition)[0];
        const [field, values] = condition[operator];

        if (field === null || field === undefined || field === '') {
          this.rootStore.notification.enqueueErrorSnackbar('Field is required');
          hasErrors = true;
          this.ruleErrors[rule.uuid] = { fieldKey: 'conditions', conditionIndex: index, error: 'Field is required' };
        }
        if (values.length === 0) {
          this.rootStore.notification.enqueueErrorSnackbar('Condition must have at least one value');
          hasErrors = true;
          this.ruleErrors[rule.uuid] = {
            fieldKey: 'conditions',
            conditionIndex: index,
            error: 'Condition must have at least one value',
          };
        }
      });
    });

    // validate pipeline assignment rules
    this.assignmentRulesPipelines.forEach((rule) => {
      if (rule.conditions.length === 0) {
        this.rootStore.notification.enqueueErrorSnackbar('Pipeline rule must have at least one condition');
        hasErrors = true;
      }
      if (rule.pipeline === undefined || !rule.pipeline.uuid || rule.pipeline.uuid === '') {
        this.rootStore.notification.enqueueErrorSnackbar('Pipeline rule must have a pipeline selected');
        hasErrors = true;
      }
      if (rule.name.length === 0) {
        this.rootStore.notification.enqueueErrorSnackbar('Pipeline rule must have a name');
        hasErrors = true;
      }
      rule.conditions.forEach((condition) => {
        const operator = Object.keys(condition)[0];
        const [field, values] = condition[operator];

        if (field === null || field === undefined || field === '') {
          this.rootStore.notification.enqueueErrorSnackbar('Field is required');
          hasErrors = true;
        }
        if (values.length === 0) {
          this.rootStore.notification.enqueueErrorSnackbar('Condition must have at least one value');
          hasErrors = true;
        }
      });
    });

    return hasErrors;
  }

  @action.bound
  async updateAssignmentRule(): Promise<boolean> {
    if (this.validateRule()) {
      return false;
    }

    try {
      this.isSaving = true;
      const dataToSend = this.assignmentRules.map((rule) => transformRuleForRequest(rule, this.isRuleEdited(rule)));

      const { data } = await this.api.entities.updateAssignmentRule(dataToSend)();

      this.initialAssignmentRules = transformValues<AssignmentRuleResponseType[]>(data);
      this.edited = false;

      this.lastPublished = new Date().toISOString();
      return true;
    } catch (error) {
      const errors = apiErrors(error as AxiosError, 'Failed to update assignment rules');
      this.assignmentRulesErrors = errors;
      // for now we don't show errors in the UI
      // errors.forEach((err) => {
      //   this.rootStore.notification.enqueueErrorSnackbar(err as string);
      // });
      return false;
    } finally {
      this.isSaving = false;
    }
  }

  @action.bound
  async updateAssignmentRulePipeline(): Promise<boolean> {
    if (this.validateRule()) {
      return false;
    }

    try {
      this.isSaving = true;
      const dataToSend = this.assignmentRulesPipelines.map((rule) => ({
        ...rule,
        pipeline: rule.pipeline.uuid,
        conditions: rule.conditions.map((condition) => {
          const operator = Object.keys(condition)[0];
          const [field, values] = condition[operator];
          if (field === OPERATORS.submission_form) {
            return {
              [OPERATORS.submission_form]: values.map((value: { uuid?: string; key?: string }) => value.key),
            };
          }
          return {
            [operator]: [field, values.map((value) => ('uuid' in value ? { uuid: value.uuid } : { key: value.key }))],
          };
        }),
      }));

      const { data } = await this.api.entities.updateAssignmentRulePipelines(
        dataToSend as AssignmentRulePipelineRequestType[]
      )();
      this.initialAssignmentRulesPipelines = transformValues<AssignmentRulePipelineResponseType[]>(data);
      this.edited = false;

      this.lastPublished = new Date().toISOString();
      return true;
    } catch (error) {
      const errors = apiErrors(error as AxiosError, 'Failed to update pipeline assignment rules');
      this.assignmentRulesPipelinesErrors = errors;
      // for now we don't show errors in the UI
      // errors.forEach((err) => {
      //   this.rootStore.notification.enqueueErrorSnackbar(err as string);
      // });
      return false;
    } finally {
      this.isSaving = false;
    }
  }

  @action.bound
  addConditionGeneric(ruleId: string): void {
    const condition: AssignmentRuleType['conditions'][number] = {
      is_any_of: ['', []],
    };

    // Try to add to regular rules first
    const regularRule = this.assignmentRules.find(({ uuid }) => uuid === ruleId);
    if (regularRule) {
      regularRule.conditions = [...regularRule.conditions, condition];
      this.assignmentRules = set(this.assignmentRules, ruleId, regularRule);
      return;
    }

    // Try to add to pipeline rules
    const pipelineRule = this.assignmentRulesPipelines.find(({ uuid }) => uuid === ruleId);
    if (pipelineRule) {
      pipelineRule.conditions = [...pipelineRule.conditions, condition];
      this.assignmentRulesPipelines = set(this.assignmentRulesPipelines, ruleId, pipelineRule);
    }
  }

  @action.bound
  updateRuleGeneric(ruleId: string, data: Partial<AssignmentRuleType>): void {
    // clear errors
    delete this.ruleErrors[ruleId];

    // Try to update in regular rules first
    const regularRuleIndex = this.assignmentRules.findIndex((rule) => rule.uuid === ruleId);
    if (regularRuleIndex !== -1) {
      this.assignmentRules[regularRuleIndex] = {
        ...this.assignmentRules[regularRuleIndex],
        ...data,
      } as AssignmentRuleResponseType;
      return;
    }

    // Try to update in pipeline rules
    const pipelineRuleIndex = this.assignmentRulesPipelines.findIndex((rule) => rule.uuid === ruleId);
    if (pipelineRuleIndex !== -1) {
      this.assignmentRulesPipelines[pipelineRuleIndex] = {
        ...this.assignmentRulesPipelines[pipelineRuleIndex],
        ...data,
      } as AssignmentRulePipelineResponseType;
    }
  }

  @action.bound
  removeConditionGeneric(ruleId: string, conditionIndex: number): void {
    // Try to remove from regular rules first
    const regularRule = this.assignmentRules.find(({ uuid }) => uuid === ruleId);
    if (regularRule) {
      regularRule.conditions.splice(conditionIndex, 1);
      return;
    }

    // Try to remove from pipeline rules
    const pipelineRule = this.assignmentRulesPipelines.find(({ uuid }) => uuid === ruleId);
    if (pipelineRule) {
      pipelineRule.conditions.splice(conditionIndex, 1);
    }
  }

  @action.bound
  updateConditionGeneric(
    ruleId: string,
    conditionIndex: number,
    condition: AssignmentRuleType['conditions'][number]
  ): void {
    // clear errors
    delete this.ruleErrors[ruleId];

    // Try to update in regular rules first
    const regularRule = this.assignmentRules.find(({ uuid }) => uuid === ruleId);
    if (regularRule) {
      regularRule.conditions[conditionIndex] = condition;
      this.assignmentRules = set(this.assignmentRules, ruleId, regularRule);
      return;
    }

    // Try to update in pipeline rules
    const pipelineRule = this.assignmentRulesPipelines.find(({ uuid }) => uuid === ruleId);
    if (pipelineRule) {
      pipelineRule.conditions[conditionIndex] = condition;
      this.assignmentRulesPipelines = set(this.assignmentRulesPipelines, ruleId, pipelineRule);
    }
  }

  @action.bound
  deleteRuleGeneric(ruleId: string): void {
    // clear errors
    delete this.ruleErrors[ruleId];

    // Try to delete from regular rules first
    const regularRuleIndex = this.assignmentRules.findIndex(({ uuid }) => uuid === ruleId);
    if (regularRuleIndex !== -1) {
      this.assignmentRules = this.assignmentRules.filter(({ uuid }) => uuid !== ruleId);
      return;
    }

    // Try to delete from pipeline rules
    const pipelineRuleIndex = this.assignmentRulesPipelines.findIndex(({ uuid }) => uuid === ruleId);
    if (pipelineRuleIndex !== -1) {
      this.assignmentRulesPipelines = this.assignmentRulesPipelines.filter(({ uuid }) => uuid !== ruleId);
    }
  }

  getValuesForField(field?: EntityFieldType): string[] {
    if (!field) {
      return [];
    }

    if (field.protectorType === ProtectorType.SingleChoice) {
      return field.properties?.enum?.map(({ key }) => key) || [];
    }

    return [];
  }

  @action.bound
  createEmptyRule(currentRuleId?: string): void {
    const emptyRule: AssignmentRuleResponseType = {
      uuid: uuidv4(),
      name: getNextRuleName(this.assignmentRules),
      createdAt: '',
      updatedAt: '',
      conditions: [
        {
          is_any_of: ['', []],
        },
      ],
      targetUsers: [],
      targetGroups: [],
    };

    if (!this.assignmentRules || this.assignmentRules.length === 0) {
      this.assignmentRules = [emptyRule];
    } else if (currentRuleId) {
      const currentRuleIndex = this.assignmentRules.findIndex(({ uuid }) => uuid === currentRuleId);

      this.assignmentRules = [
        ...this.assignmentRules.slice(0, currentRuleIndex + 1),
        emptyRule,
        ...this.assignmentRules.slice(currentRuleIndex + 1),
      ];
    } else {
      this.assignmentRules = [...this.assignmentRules, emptyRule];
    }
  }

  @action.bound
  createEmptyRulePipeline(currentRuleId?: string): void {
    const emptyRulePipeline: AssignmentRulePipelineResponseType = {
      uuid: uuidv4(),
      name: getNextRuleName(this.assignmentRulesPipelines),
      createdAt: '',
      updatedAt: '',
      conditions: [
        {
          is_any_of: ['', []],
        },
      ],
      pipeline: {
        uuid: '',
        name: '',
      },
    };

    if (!this.assignmentRulesPipelines || this.assignmentRulesPipelines.length === 0) {
      this.assignmentRulesPipelines = [emptyRulePipeline];
    } else if (currentRuleId) {
      const currentRuleIndex = this.assignmentRulesPipelines.findIndex(({ uuid }) => uuid === currentRuleId);
      this.assignmentRulesPipelines = [
        ...this.assignmentRulesPipelines.slice(0, currentRuleIndex + 1),
        emptyRulePipeline,
        ...this.assignmentRulesPipelines.slice(currentRuleIndex + 1),
      ];
    } else {
      this.assignmentRulesPipelines = [...this.assignmentRulesPipelines, emptyRulePipeline];
    }
  }
}

export default AssignmentRules;
